import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { SignOut, ChartLineUp, ChatCircleText } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { getMyCredits } from "../lib/api";
import { FeedbackDialog } from "./FeedbackDialog";

/**
 * V1 TopBar — brand mark left, credits pill right, user menu far right.
 * Mirrors the prototype's `header.top`.
 */
export const TopBar = () => {
  const { user, logout } = useAuth();
  const [credits, setCredits] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    // Only fetch credits once auth is established; otherwise /me/credits 401s silently
    // and the chip never appears (Mac was most affected due to slower cookie set-up).
    if (!user) {
      setCredits(null);
      return;
    }
    let alive = true;
    const load = () =>
      getMyCredits()
        .then((c) => alive && setCredits(c))
        .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [user?.user_id]);

  const creditsLabel = credits
    ? credits.scouts_remaining > 0
      ? `${credits.scouts_remaining} of ${credits.scouts_per_day_free} free scouts left today`
      : `0 of ${credits.scouts_per_day_free} free scouts left · back tomorrow`
    : "";

  return (
    <header
      className="flex items-center justify-between gap-4 px-6 pt-7 max-w-[920px] mx-auto"
      data-testid="top-bar"
    >
      <Link
        to="/"
        className="flex items-baseline gap-2 cursor-pointer hover:opacity-80 transition-opacity"
        data-testid="brand-link"
        aria-label="Recce home"
      >
        <span className="font-serif text-[21px] font-semibold tracking-[0.2px] text-amber">
          Recce
        </span>
      </Link>

      <div className="flex items-center gap-3">
        {creditsLabel && (
          <span
            className="hidden sm:inline-flex text-[12px] text-ink-dim border border-line rounded-full px-3.5 py-1.5 whitespace-nowrap"
            data-testid="credits-pill"
          >
            {creditsLabel}
          </span>
        )}
        {user?.is_owner && (
          <Link
            to="/stats"
            className="p-2 text-ink-dim hover:text-amber"
            title="stats"
            data-testid="nav-stats"
            aria-label="stats"
          >
            <ChartLineUp size={16} weight="regular" />
          </Link>
        )}
        <button
          type="button"
          onClick={() => setFeedbackOpen(true)}
          className="p-2 text-ink-dim hover:text-amber"
          title="send feedback"
          data-testid="nav-feedback"
          aria-label="send feedback"
        >
          <ChatCircleText size={16} />
        </button>
        {user && (
          <div className="flex items-center gap-2" data-testid="topbar-user">
            {user.picture ? (
              <img
                src={user.picture}
                alt=""
                className="w-7 h-7 rounded-full border border-line object-cover"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            ) : (
              <div className="w-7 h-7 rounded-full border border-line bg-surface flex items-center justify-center font-serif text-sm">
                {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <button
              type="button"
              onClick={logout}
              className="p-1.5 text-ink-dim hover:text-amber"
              title="sign out"
              data-testid="topbar-logout"
              aria-label="sign out"
            >
              <SignOut size={14} />
            </button>
          </div>
        )}
      </div>

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </header>
  );
};
